import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';

const DEFAULT_SIZES = [9, 18, 36, 50];

// Build a compact page list with ellipses, e.g. [1, '…', 4, 5, 6, '…', 12].
function pageList(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const pages = new Set([1, pageCount, page, page - 1, page + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push(`gap-${p}`);
    out.push(p);
    prev = p;
  }
  return out;
}

// Reusable pager (prev / pages / next) with an optional "N per page" selector.
// Presentational: the parent owns `page`/`pageCount` (and optionally `pageSize`).
// Pass `pageSize` + `onPageSizeChange` to show the size selector. Renders nothing
// when there is a single page and no selector to show.
export default function Pagination({
  page,
  pageCount,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizes = DEFAULT_SIZES,
  className = '',
}) {
  const t = useT();
  // Only surface the size selector when there is actually something to page
  // through (more than one page, or a larger size the user could dial back).
  const hasSelector = typeof pageSize === 'number' && typeof onPageSizeChange === 'function';
  const showSelector = hasSelector && (pageCount > 1 || pageSize > pageSizes[0]);
  if ((!pageCount || pageCount <= 1) && !showSelector) return null;

  const go = (p) => {
    const next = Math.min(pageCount, Math.max(1, p));
    if (next !== page) onPageChange(next);
  };

  const base = 'flex h-10 min-w-10 items-center justify-center rounded-lg border px-3 text-sm font-semibold transition';

  return (
    <div className={`mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row sm:justify-between ${className}`}>
      {showSelector ? (
        <label className="flex items-center gap-2 text-sm text-gray-500">
          {t('pagination.perPage')}
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-[#14213d] focus:border-[#1fa5a3] focus:outline-none"
          >
            {pageSizes.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      ) : <span className="hidden sm:block" />}

      {pageCount > 1 ? (
        <nav className="flex flex-wrap items-center justify-center gap-2" aria-label={t('pagination.label')}>
          <button
            type="button"
            onClick={() => go(page - 1)}
            disabled={page <= 1}
            aria-label={t('pagination.prev')}
            className={`${base} border-gray-200 text-[#14213d] hover:border-[#1fa5a3] hover:text-[#1fa5a3] disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {pageList(page, pageCount).map((p) =>
            typeof p === 'string' ? (
              <span key={p} className="px-1 text-gray-400">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => go(p)}
                aria-current={p === page ? 'page' : undefined}
                className={`${base} ${p === page ? 'border-[#1fa5a3] bg-[#1fa5a3] text-white' : 'border-gray-200 text-[#14213d] hover:border-[#1fa5a3] hover:text-[#1fa5a3]'}`}
              >
                {p}
              </button>
            )
          )}

          <button
            type="button"
            onClick={() => go(page + 1)}
            disabled={page >= pageCount}
            aria-label={t('pagination.next')}
            className={`${base} border-gray-200 text-[#14213d] hover:border-[#1fa5a3] hover:text-[#1fa5a3] disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      ) : null}
    </div>
  );
}
